/* ========================================================================
   PC 版: ギャラリー sticky 制御（CSS sticky 代替）
   CSS sticky はコンテナ高さに依存するため、アコーディオン開閉でグリッド行の
   高さが変動するとギャラリー位置がジャンプする。
   transform: translateY で JS 側から位置を制御し、ジャンプを完全に排除する。
   ======================================================================== */

(function initGallerySticky() {
  /* product swap (combined listing) で script が再実行されるたびに
     前回のリスナーが積み残るのを防ぐ */
  if (typeof window.__auremeGalleryStickyCleanup === 'function') {
    window.__auremeGalleryStickyCleanup();
  }

  const MQ = window.matchMedia('(min-width: 990px)');
  const pdp = document.querySelector('.product--aureme-pdp');
  if (!pdp) return;

  const gallery = pdp.querySelector('media-gallery');
  const wrapper = pdp.querySelector('.product__media-wrapper');
  const infoWrapper = pdp.querySelector('.product__info-wrapper');
  if (!gallery || !wrapper || !infoWrapper) return;

  /* enable_sticky_info が無効なら product__column-sticky が付かないので初期化しない */
  if (!gallery.classList.contains('product__column-sticky')) return;

  const computeStickyTop = () => {
    const headerPx =
      parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--header-height')) ||
      0;
    /* 2rem を html の実フォントサイズで px 換算する（body_scale 設定対応） */
    const remPx = parseFloat(getComputedStyle(document.documentElement).fontSize) || 10;
    return headerPx + remPx * 2;
  };

  let stickyTop = computeStickyTop();
  let currentOffset = 0;
  let rafHandle = 0;
  let headerObserverRaf = 0;
  let pendingAllowIncrease = false;
  let lastScrollY = window.scrollY;
  const OFFSET_EPSILON = 0.01;

  /* media-gallery の position: relative; top: 3rem による CSS オフセット
     750px 未満では top が無効のため、breakpoint 変更後に再取得する */
  let galleryBaseOffset = parseFloat(getComputedStyle(gallery).top) || 0;

  /**
   * ギャラリーの位置がとるべき offset の範囲を計算する。
   * offset = 0 は自然配置、offset > 0 はヘッダー下に固定。
   * gallery の視覚位置 = wrapperTop + galleryBaseOffset + offset
   * 固定時の視覚位置 = stickyTop + galleryBaseOffset
   */
  function computeOffsetState() {
    const wrapperTop = wrapper.getBoundingClientRect().top;
    const desiredOffset = wrapperTop >= stickyTop ? 0 : stickyTop - wrapperTop;

    const galleryH = gallery.offsetHeight;
    /* 右カラムの底を上限にする（wrapper は stretch で伸びるため基準にしない） */
    const contentBottom = infoWrapper.getBoundingClientRect().bottom;
    const maxOffset = Math.max(0, contentBottom - wrapperTop - galleryBaseOffset - galleryH);
    const idealOffset = Math.min(desiredOffset, maxOffset);

    return {
      desiredOffset,
      idealOffset,
      maxOffset,
    };
  }

  function applyOffset(offset) {
    const nextOffset = Math.max(0, offset);
    if (nextOffset <= OFFSET_EPSILON) {
      gallery.style.transform = '';
      currentOffset = 0;
      return;
    }

    const appliedOffset = Number(nextOffset.toFixed(3));
    gallery.style.transform = `translateY(${appliedOffset.toFixed(3)}px)`;
    currentOffset = appliedOffset;
  }

  /** desktop 判定付きで理想 offset を適用する（初期化と更新で共通） */
  function updateOffset({ allowIncrease = true, useScrollDelta = false } = {}) {
    const scrollY = window.scrollY;
    const scrollDelta = scrollY - lastScrollY;
    lastScrollY = scrollY;

    if (!MQ.matches) {
      if (currentOffset > OFFSET_EPSILON) applyOffset(0);
      return;
    }

    const { desiredOffset, idealOffset, maxOffset } = computeOffsetState();
    let nextOffset = idealOffset;

    if (desiredOffset <= OFFSET_EPSILON) {
      if (currentOffset > OFFSET_EPSILON) applyOffset(0);
      return;
    }

    if (!allowIncrease) {
      if (currentOffset <= idealOffset + OFFSET_EPSILON) return;

      const overLimitOffset = currentOffset - idealOffset;
      applyOffset(idealOffset);

      /*
        画像の見た目位置を保つ分だけページを上へ戻し、
        右カラム下端やフッターが画像へ重ならないようにする。
      */
      const nextScrollY = Math.max(0, scrollY - overLimitOffset);
      if (Math.abs(nextScrollY - scrollY) > OFFSET_EPSILON) {
        window.scrollTo(window.scrollX, nextScrollY);
        lastScrollY = window.scrollY;
      }
      return;
    }

    if (useScrollDelta) {
      const isOverContentLimit = currentOffset > idealOffset + OFFSET_EPSILON;
      const isBottomLimited =
        currentOffset >= maxOffset - OFFSET_EPSILON && desiredOffset > maxOffset + OFFSET_EPSILON;
      const wasBottomLimitedBeforeScroll =
        scrollDelta < -OFFSET_EPSILON &&
        currentOffset >= maxOffset - OFFSET_EPSILON &&
        desiredOffset - scrollDelta > maxOffset + OFFSET_EPSILON;

      if (isOverContentLimit) {
        /*
          アコーディオン閉じなどで右カラムが急に短くなった直後は、
          スクロール入力を超えない範囲で少しずつ制限内へ戻す。
        */
        const overLimitCorrection =
          scrollDelta > OFFSET_EPSILON ? Math.min(currentOffset - idealOffset, 2) : 0;
        nextOffset = currentOffset + Math.min(scrollDelta, 0) - overLimitCorrection;
        nextOffset = Math.max(idealOffset, nextOffset);
      } else {
        if (desiredOffset <= maxOffset + OFFSET_EPSILON && wasBottomLimitedBeforeScroll) {
          /* 下端制限を抜けたら、通常の固定位置へ戻して上方向に残らないようにする */
          nextOffset = idealOffset;
        } else {
          /* 下端制限中に上へ戻る場合は maxOffset を保ち、画像が固定位置へ跳ね戻るのを避ける */
          nextOffset =
            scrollDelta < -OFFSET_EPSILON && isBottomLimited
              ? maxOffset
              : currentOffset + scrollDelta;
          nextOffset = Math.max(0, Math.min(nextOffset, idealOffset));
        }
      }
    }

    if (Math.abs(Math.max(0, nextOffset) - currentOffset) < OFFSET_EPSILON) return;
    applyOffset(nextOffset);
  }

  /**
   * 複数経路の更新を1フレームに集約し、transform の微小な往復更新を防ぐ。
   * allowIncrease=false は高さ縮小時に画像位置を保ったまま下端だけ詰める。
   */
  function scheduleUpdate({ allowIncrease = true } = {}) {
    pendingAllowIncrease = pendingAllowIncrease || allowIncrease;
    if (rafHandle) return;
    rafHandle = window.requestAnimationFrame(() => {
      const allowIncreaseInFrame = pendingAllowIncrease;
      pendingAllowIncrease = false;
      rafHandle = 0;
      updateOffset({
        allowIncrease: allowIncreaseInFrame,
      });
    });
  }

  function onScroll() {
    updateOffset({ useScrollDelta: true });
  }

  function preserveCurrentOffset() {
    updateOffset({ allowIncrease: false });
  }

  function schedulePreserveCurrentOffset() {
    scheduleUpdate({ allowIncrease: false });
  }

  /** desktop 判定付きで理想 offset を適用する（resize と初期化で共通） */
  function resetOffset() {
    stickyTop = computeStickyTop();
    galleryBaseOffset = parseFloat(getComputedStyle(gallery).top) || 0;
    updateOffset();
  }

  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', resetOffset);

  /* --header-height がフォント読み込みや ResizeObserver で後から変わるケースに追従 */
  const headerObserver = new MutationObserver(() => {
    if (headerObserverRaf) return;
    headerObserverRaf = window.requestAnimationFrame(() => {
      headerObserverRaf = 0;
      const newTop = computeStickyTop();
      if (Math.abs(newTop - stickyTop) >= 1) {
        stickyTop = newTop;
        scheduleUpdate();
      }
    });
  });
  headerObserver.observe(document.documentElement, {
    attributes: true,
    attributeFilter: ['style'],
  });

  /* アコーディオン開閉によるコンテンツ高さ変動に追従 */
  const accordionDetails = Array.from(pdp.querySelectorAll('.aureme-product-accordion'));
  for (const details of accordionDetails) {
    details.addEventListener('toggle', preserveCurrentOffset);
  }

  /* 右カラムとギャラリーの高さ縮小時は、画像位置を保ったまま下端だけ詰める */
  let contentResizeObserver;
  if (typeof ResizeObserver !== 'undefined') {
    contentResizeObserver = new ResizeObserver(schedulePreserveCurrentOffset);
    contentResizeObserver.observe(infoWrapper);
    contentResizeObserver.observe(gallery);
  }

  /* product swap で再実行された際に前回のリスナーを解除するクリーンアップ */
  window.__auremeGalleryStickyCleanup = () => {
    window.removeEventListener('scroll', onScroll);
    window.removeEventListener('resize', resetOffset);
    headerObserver.disconnect();
    contentResizeObserver?.disconnect();
    for (const details of accordionDetails) {
      details.removeEventListener('toggle', preserveCurrentOffset);
    }
    if (rafHandle) {
      window.cancelAnimationFrame(rafHandle);
      rafHandle = 0;
    }
    if (headerObserverRaf) {
      window.cancelAnimationFrame(headerObserverRaf);
      headerObserverRaf = 0;
    }
    applyOffset(0);
  };

  /* 初期位置を設定（ページロード時に既にスクロール済みの場合に対応） */
  resetOffset();
})();
